import React, { useState, useEffect } from 'react';
import './AdminOrders.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

const getAuthToken = () => {
  const token = localStorage.getItem('access_token');
  return token;
};

const AdminOrders = ({ setCurrentPage, setPageData }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, filterStatus]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('per_page', 20);
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await fetch(`${API_BASE_URL}/orders?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setOrders(data.data.orders || []);
        if (data.data.pagination) {
          setTotalPages(data.data.pagination.total_pages || 1);
        }
      } else {
        setError(data.message || 'Failed to fetch orders');
        setOrders([]);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication required. Please log in.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status: newStatus,
          notes: `Status changed to ${newStatus} by admin`
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessMessage(`Order status updated to ${newStatus}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrders(); // Refresh orders
      } else {
        setError(data.message || 'Failed to update order status');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status');
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication required. Please log in.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: 'Cancelled by administrator'
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessMessage('Order cancelled successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrders(); // Refresh orders
      } else {
        setError(data.message || 'Failed to cancel order');
      }
    } catch (err) {
      console.error('Error cancelling order:', err);
      setError('Failed to cancel order');
    }
  };

  const handleViewDetails = (order) => {
    setPageData({ 
      orderId: order.id,
      orderNumber: order.order_number 
    });
    setCurrentPage('admin-order-details');
  };

  const handleSearch = () => {
    setCurrentPageNum(1);
    fetchOrders();
  };

  // Filter orders locally as well (for search)
  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.customer_info?.name?.toLowerCase().includes(search) ||
      order.customer_info?.email?.toLowerCase().includes(search)
    );
  });

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return statusClasses[status?.toLowerCase()] || 'status-pending';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return `$${numPrice.toFixed(2)}`;
  };

  const getOrderStats = () => {
    return {
      total: orders.length,
      pending: orders.filter(o => o.status?.toLowerCase() === 'pending').length,
      processing: orders.filter(o => ['processing', 'confirmed'].includes(o.status?.toLowerCase())).length,
      delivered: orders.filter(o => o.status?.toLowerCase() === 'delivered').length
    };
  };

  const stats = getOrderStats();

  if (loading && orders.length === 0) {
    return (
      <div className="admin-orders-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-orders-container">
      <div className="orders-header">
        <h1>Order Management</h1>
        <p>Manage and track all customer orders</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Filters and Search */}
      <div className="orders-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by order number, customer name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>Search</button>
        </div>

        <div className="filter-box">
          <label>Filter by Status:</label>
          <select 
            value={filterStatus} 
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPageNum(1);
            }}
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <button className="btn-refresh" onClick={fetchOrders}>
          ↻ Refresh
        </button>
      </div>

      {/* Orders Statistics */}
      <div className="orders-stats">
        <div className="stat-card">
          <h3>Total Orders</h3>
          <p className="stat-number">{stats.total}</p>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p className="stat-number">{stats.pending}</p>
        </div>
        <div className="stat-card">
          <h3>Processing</h3>
          <p className="stat-number">{stats.processing}</p>
        </div>
        <div className="stat-card">
          <h3>Completed</h3>
          <p className="stat-number">{stats.delivered}</p>
        </div>
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>📭 No orders found</p>
          {searchTerm && (
            <button onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
              fetchOrders();
            }}>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="order-number">{order.order_number}</td>
                  <td>
                    <div className="customer-info">
                      <strong>{order.customer_info?.name || 'N/A'}</strong>
                      <small>{order.customer_info?.email || 'N/A'}</small>
                    </div>
                  </td>
                  <td>{formatDate(order.timestamps?.created_at || order.created_at)}</td>
                  <td>{order.item_count || order.items?.length || 0} item(s)</td>
                  <td className="order-total">
                    {formatPrice(order.pricing?.total_amount || order.totals?.total || order.total || 0)}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="order-actions">
                    <button 
                      className="btn-view"
                      onClick={() => handleViewDetails(order)}
                      title="View Details"
                    >
                      👁️ View
                    </button>
                    
                    <select
                      className="status-select"
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      title="Change Status"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    
                    {order.status?.toLowerCase() !== 'cancelled' && (
                      <button 
                        className="btn-delete"
                        onClick={() => handleCancelOrder(order.id)}
                        title="Cancel Order"
                      >
                        ✕ Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPageNum(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPageNum(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {/* Back Button */}
      <div className="orders-footer">
        <button 
          className="btn-back"
          onClick={() => setCurrentPage('admin-dashboard')}
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default AdminOrders;